import React from 'react'
import PropTypes from 'prop-types'
import { FormTextField, useZionFormContext, useFormState } from '@fs/zion-form'
import { Row, Cell, LabelSpacer } from '@fs/zion-ui'
import { useSlsStateContext, useSlsHighlightsContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import HighlightButton from '../../panels/shared/HighlightButton'
import { prepareFieldForHighlights, handleInputBlur, handleInputFocus } from '../../shared/utils/highlightUtils'
import { validateLinkedEventFieldChange, getOriginalFormValue } from '../utils/events.utils'

/**
 * Generic SubElements renderer
 * Renders either a suggest component (passed via `SuggestComponent`) when
 * `fieldType === suggestFieldType` or a text field for other field types.
 *
 * @param {Object} props
 * @param {Array<{id: (string|number), fieldType: string, displayLabel?: string}>} props.subElements - Array of sub-element objects to render.
 * @param {number} props.eventIndex - Index of the event in the parent array.
 * @param {number} props.itemIndex - Index of the item in the parent array.
 * @param {string} props.indexName - Name of the index (e.g., 'primary', 'additional').
 * @param {{ primary: boolean }} props.field - Field object indicating if primary.
 * @param {function} props.t - Translation function.
 * @param {React.ComponentType} [props.SuggestComponent] - Component to render for suggest fields.
 * @param {string} [props.suggestFieldType] - Field type that triggers suggest rendering.
 * @param {string} [props.suggestLabelKey] - i18n key for suggest label.
 * @param {string} [props.suggestDefaultLabel] - Default label for suggest field.
 * @returns {React.ReactNode} Rendered sub-elements.
 */
const SubElements = ({
  subElements,
  eventIndex,
  itemIndex,
  indexName,
  field,
  t,
  SuggestComponent,
  suggestFieldType,
  suggestLabelKey,
  suggestDefaultLabel,
  isLinkedEvent = false,
  primaryEvent = false,
  splitScreenImageArk,
}) => {
  const { getValues, control } = useZionFormContext()
  const { slsTypes, images } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const [tTranslation] = useTranslation()
  const { defaultValues } = useFormState({ control })

  if (!subElements || subElements.length === 0) {
    return null
  }

  return subElements.map(({ id, fieldType, displayLabel, typeData = { editable: true } }, subElIndex) => {
    const basePath = `events.${field.primary ? 'primary' : 'additional'}[${eventIndex}].${indexName}[${itemIndex}].subElements[${subElIndex}]`

    if (fieldType === suggestFieldType && SuggestComponent) {
      const Suggest = SuggestComponent
      const suggestTestId = `${indexName.slice(0, -1)}-field`
      return (
        <Row gutters="xxs" key={`sub-${id || subElIndex}`}>
          <Suggest
            name={`${basePath}.formValue`}
            label={t(suggestLabelKey, suggestDefaultLabel)}
            data-testid={`${suggestTestId}-${itemIndex}`}
            warningValidate={(val) => {
              const originalValue = getOriginalFormValue(defaultValues, primaryEvent, eventIndex, indexName, itemIndex)
              return validateLinkedEventFieldChange(isLinkedEvent, val, originalValue, t)
            }}
            standardize
            onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
            onBlur={() => handleInputBlur(h)}
            disabled={!typeData?.editable}
            growX
            dense
          />
          <Cell>
            <LabelSpacer />
            <HighlightButton
              field={prepareFieldForHighlights(getValues, fieldType, basePath, slsTypes, images)}
              splitScreenImageArk={splitScreenImageArk}
              buttonProps={{ 'data-testid': `${indexName}-highlight-button` }}
              linkName={`essential-information-${indexName}-highlight`}
              disabled={!typeData?.editable}
              growX
              iconOnly
            />
          </Cell>
        </Row>
      )
    }

    const textTestId = `${indexName.slice(0, -1)}-field`

    return (
      <Row gutters="xxs" key={`sub-${id || subElIndex}`}>
        <FormTextField
          name={`${basePath}.formValue.primaryText`}
          label={displayLabel}
          data-testid={textTestId}
          warningValidate={(val) => {
            const originalValue = getOriginalFormValue(
              defaultValues,
              field.primary,
              eventIndex,
              indexName,
              itemIndex,
              subElIndex
            )
            return validateLinkedEventFieldChange(isLinkedEvent, val, originalValue, tTranslation)
          }}
          onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
          onBlur={() => handleInputBlur(h)}
          disabled={!typeData?.editable}
          growX
          dense
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights(getValues, fieldType, basePath, slsTypes, images)}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{ 'data-testid': `${indexName}-highlight-button` }}
            linkName={`essential-information-${indexName}-highlight`}
            disabled={!typeData?.editable}
            growX
            iconOnly
          />
        </Cell>
      </Row>
    )
  })
}

SubElements.propTypes = {
  subElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fieldType: PropTypes.string.isRequired,
      displayLabel: PropTypes.string,
      typeData: PropTypes.shape({
        editable: PropTypes.bool,
      }),
    })
  ).isRequired,
  eventIndex: PropTypes.number.isRequired,
  itemIndex: PropTypes.number.isRequired,
  indexName: PropTypes.string.isRequired,
  field: PropTypes.shape({ primary: PropTypes.bool }).isRequired,
  t: PropTypes.func.isRequired,
  SuggestComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  suggestFieldType: PropTypes.string,
  suggestLabelKey: PropTypes.string,
  suggestDefaultLabel: PropTypes.string,
  isLinkedEvent: PropTypes.bool,
}

export default SubElements
